import { isEnumMember } from '../../../helpers/types'
import {
  dateFormatsRe,
  decimalFormatRe,
  integerFormatRe,
} from '../../../helpers/validators'
import {
  DateFormat,
  DateMatch,
  DateTimeFormat,
  DateTimeMatch,
  NumericFormat,
  NumericMatch,
  TextMatch,
} from '../../../types/FilterCondition'
import {
  type UiDateSpec,
  type UiDateTimeSpec,
  type UiNumericSpec,
  type UiProfileSpec,
  UiTextFormat,
  type UiTextSpec,
} from '../filterConditionTypes'
import { DATEMATCHGROUPS } from './conditionOptions'

export const validateUiTextSpec = (uiSpec: UiProfileSpec): boolean => {
  return (
    isEnumMember<TextMatch>(uiSpec.Match, TextMatch) &&
    uiSpec.Identifier !== undefined &&
    isEnumMember<UiTextFormat>(uiSpec.Format, UiTextFormat) &&
    ((!textMatchHasNoValue(uiSpec.Match) &&
      typeof uiSpec.Value === 'string' &&
      uiSpec.Value !== '') ||
      (textMatchHasNoValue(uiSpec.Match) &&
        (uiSpec.Value === undefined || uiSpec.Value === '')))
  )
}
export function isUiTextSpec(uiSpec: UiProfileSpec): uiSpec is UiTextSpec {
  return validateUiTextSpec(uiSpec)
}
export const textMatchHasMultipleValues = (op?: TextMatch): boolean => {
  return op === TextMatch.TextIn || op === TextMatch.TextNotIn
}
export const textMatchHasNoValue = (op?: TextMatch): boolean => {
  return op === TextMatch.TextNullEmpty || op === TextMatch.TextNotNullNotEmpty
}

export const validateUiNumericSpec = (uiSpec: UiProfileSpec): boolean => {
  return (
    isEnumMember<NumericMatch>(uiSpec.Match, NumericMatch) &&
    uiSpec.Identifier !== undefined &&
    isEnumMember<NumericFormat>(uiSpec.Format, NumericFormat) &&
    uiSpec.Value !== undefined &&
    typeof uiSpec.Value === 'number' &&
    validateNumericValue(uiSpec)
  )
}
export const validateNumericValue = (uiSpec: UiProfileSpec): boolean => {
  const asString = `${uiSpec.Value}`
  const rawDigits = asString.replace(/\D+/g, '')
  switch (uiSpec.Format) {
    case NumericFormat.Decimal:
      return decimalFormatRe.test(asString) && rawDigits.length <= 7
    case NumericFormat.Integer:
      return integerFormatRe.test(asString) && rawDigits.length <= 7
    default:
      return false
  }
}
export function isUiNumericSpec(
  uiSpec: UiProfileSpec,
): uiSpec is UiNumericSpec {
  return validateUiNumericSpec(uiSpec)
}

export const validateUiDateSpec = (uiSpec: UiProfileSpec): boolean => {
  return (
    isEnumMember<DateMatch>(uiSpec.Match, DateMatch) &&
    uiSpec.Identifier !== undefined &&
    isEnumMember<DateFormat>(uiSpec.Format, DateFormat) &&
    ((dateMatchHasNoValue(uiSpec.Match) &&
      (uiSpec.Value === undefined || uiSpec.Value === '')) ||
      (dateMatchHasValueInteger(uiSpec.Match) &&
        validateIntegerValue(uiSpec)) ||
      (dateMatchHasValueDateString(uiSpec.Match) &&
        validateDateStringValue(uiSpec)))
  )
}
export const validateIntegerValue = (uiSpec: UiProfileSpec): boolean => {
  if (typeof uiSpec.Value !== 'number') {
    return false
  }
  return integerFormatRe.test(`${uiSpec.Value}`)
}
const validateDateStringValue = (uiSpec: UiProfileSpec): boolean => {
  if (
    typeof uiSpec.Value !== 'string' ||
    !isEnumMember<DateFormat>(uiSpec.Format, DateFormat)
  ) {
    return false
  }
  const re = (dateFormatsRe as Record<string, RegExp>)[uiSpec.Format]
  return re.test(`${uiSpec.Value}`)
}
export function isUiDateSpec(uiSpec: UiProfileSpec): uiSpec is UiDateSpec {
  return validateUiDateSpec(uiSpec)
}
export const dateMatchHasNoValue = (op: DateMatch): boolean => {
  return op === DateMatch.EqualToToday
}
export const dateMatchHasValueInteger = (op: DateMatch): boolean => {
  return DATEMATCHGROUPS.CALCULATED.includes(op)
}
export const dateMatchHasValueDateString = (op: DateMatch): boolean => {
  return DATEMATCHGROUPS.FIXED.includes(op) && !dateMatchHasNoValue(op)
}

export const validateUiDateTimeSpec = (uiSpec: UiProfileSpec): boolean => {
  return (
    isEnumMember<DateTimeMatch>(uiSpec.Match, DateTimeMatch) &&
    uiSpec.Identifier !== undefined &&
    isEnumMember<DateTimeFormat>(uiSpec.Format, DateTimeFormat) &&
    validateIntegerValue(uiSpec)
  )
}
export function isUiDateTimeSpec(
  uiSpec: UiProfileSpec,
): uiSpec is UiDateTimeSpec {
  return validateUiDateTimeSpec(uiSpec)
}
